import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
} from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { InboxConversation, formatConversationDate } from '@/types/residents/marketplaceListing';

const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

interface InboxConversationCardProps {
  conversation: InboxConversation;
  onPress: () => void;
}

export const InboxConversationCard: React.FC<InboxConversationCardProps> = ({
  conversation,
  onPress,
}) => {
  const { listing, otherUser, lastMessage, isUnread } = conversation;
  const formattedDate = formatConversationDate(lastMessage.timestamp);

  return (
    <TouchableOpacity
      style={[
        styles.container,
        isUnread && styles.containerUnread,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {/* Listing Thumbnail */}
      {listing.image ? (
        <Image
          source={{ uri: listing.image }}
          style={styles.thumbnail}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.thumbnail, styles.thumbnailPlaceholder]}>
          <PropertyPlaceholder width={36} height={36} />
        </View>
      )}

      {/* Content */}
      <View style={styles.content}>
        {/* Top Row: Title + Date */}
        <View style={styles.topRow}>
          <View style={styles.titleContainer}>
            <Text style={styles.title} numberOfLines={1}>
              {listing.title}
            </Text>
          </View>
          <Text style={styles.date}>{formattedDate}</Text>
        </View>

        {/* Message Preview */}
        <View style={styles.messageRow}>
          <Text style={styles.messagePreview} numberOfLines={1}>
            {lastMessage.text}
          </Text>
        </View>

        {/* Seller Name */}
        <Text style={styles.userName} numberOfLines={1}>
          {otherUser.name}
        </Text>
      </View>

      {/* Unread Indicator */}
      {isUnread && <View style={styles.unreadIndicator} />}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 24,
    paddingVertical: 14,
    gap: 16,
    position: 'relative',
  },
  containerUnread: {
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
  },
  thumbnail: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
  },
  thumbnailPlaceholder: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 4,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  titleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  date: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textMuted,
    lineHeight: 18,
    textAlign: 'right',
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  messagePreview: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.gray200,
    lineHeight: 21,
  },
  userName: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textMuted,
    lineHeight: 18,
  },
  unreadIndicator: {
    position: 'absolute',
    right: 24,
    top: '80%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: MARKETPLACE_COLORS.productivityOrange,
    transform: [{ translateY: -4 }],
  },
});

